import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { Typography, Banner } from "@bca/ui-library";

import BidNowBuyNow from "./buyingOptions/BidNowBuyNow";
import BidNowBuyNowSkeleton from "./buyingOptions/BidNowBuyNowSkeleton";
import ErrorState from "./ErrorState";
import NoOptionAvailable from "./NoOptionAvailable";
import UserBidRights from "./buyingOptions/UserBidRights";
import placeBid from "../api/placeBid";
import fetchBidInfo from "../api/fetchBidInfo";
import useAsync from "../hooks/useAsync.hook";
import acceptTermsAndConditions from "./buyingOptions/acceptTermsAndConditions";
import { getSaleDate, getSaleTime } from "./SaleDateTimeBNBN";
import SuccessfulBuyNow from "./buyingOptions/SuccessfulBuyNow";
import SoldVehicleCard from "./SoldVehicleCard";
import { useAuth } from "@bca-auth0/bca-auth0-provider";

export interface BidNowBuyNowWithFetchProps {
  biddingInfoUrl: string;
  placeBidUrl: string;
  acceptTermsAndConditionsUrl: string;
  isTracked: boolean;
  vrm: string | string[];
  setIsTracked: (value: boolean) => void;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
}

interface TermsAndConditions {
  termsAndConditionsNeedAccepting: boolean;
  termsAndConditionsLink: string;
  links: {
    rel: string;
    href: string;
  }[];
  degradedServices: string[];
}

interface BidInfo {
  userCanPlaceBid: boolean;
  vrm: string;
  lotId: number;
  bidIncrement: number;
  startingBidAmount: number;
  currentBidAmount: number;
  myMaxProxy: number;
  buyNowPrice: number;
  biddingViewType: string;
  currencyCode: string;
  reserveMet: true;
  userBidStatus: string;
  lotStatus: string;
  saleEndDate: string;
  saleStartDate: string;
  saleStatus: string;
  bidHistory: {
    bidderName: string;
    placedByName: string;
    bidDateTime: string;
    bidAmount: number;
  }[];
  degradedServices: string[];
}

interface ErrorPlaceBNBNProps {
  closeBanner: () => void;
  placeBidStatus: string;
}

const ErrorPlaceBNBN: React.FunctionComponent<ErrorPlaceBNBNProps> = ({
  closeBanner,
  placeBidStatus,
}) => {
  return (
    <Banner
      variant="warning"
      title="Service currently unavailable"
      primaryLinkLabel="Close"
      primaryLinkAction={closeBanner}
      paddingX={[20, 100, 20]}
      paddingY={20}
    >
      <Typography margin={0} variant="body" color="grey-blue">
        {placeBidStatus === "error"
          ? "We're sorry, a problem occurred when placing your bid. Please try again."
          : "We're sorry, a problem occurred when purchasing your vehicle, please try again."}
      </Typography>
    </Banner>
  );
};

const BidNowBuyNowWithFetch: React.FunctionComponent<
  BidNowBuyNowWithFetchProps
> = ({
  biddingInfoUrl,
  placeBidUrl,
  acceptTermsAndConditionsUrl,
  isTracked,
  vrm,
  setIsTracked,
  inventoryId,
  isToUseAuthZero = false,
}) => {
  const { accessToken } = useAuth(!isToUseAuthZero);
  const [info, setInfo] = useState(null);
  const [bidAmount, setBidAmount] = useState(null);
  const [termsAndConditionsNeedAccepting, setTermsAndConditionsNeedAccepting] =
    useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const noOptionAvailableDate = `${getSaleDate(
    info?.saleStartDate
  )} ${getSaleTime(info?.saleStartDate)}`;

  const { data: bidInfo, error: bidInfoError } = useSWR<BidInfo>(
    (biddingInfoUrl && !isToUseAuthZero) ||
      (biddingInfoUrl && isToUseAuthZero && accessToken)
      ? [biddingInfoUrl, isToUseAuthZero, accessToken]
      : null,
    fetchBidInfo,
    { shouldRetryOnError: false }
  );

  const { data: termsAndConditions, error: termsAndConditionsError } =
    useSWR<TermsAndConditions>(
      (acceptTermsAndConditionsUrl && !isToUseAuthZero) ||
        (acceptTermsAndConditionsUrl && isToUseAuthZero && accessToken)
        ? [acceptTermsAndConditionsUrl, "get", isToUseAuthZero, accessToken]
        : null,
      acceptTermsAndConditions,
      { shouldRetryOnError: false }
    );

  const buyNowAmount = bidInfo ? bidInfo.buyNowPrice : null;

  const { execute: getBiddingInfo, value: biddingInfoResponse } = useAsync(
    () => fetchBidInfo(biddingInfoUrl, isToUseAuthZero, accessToken),
    false
  );

  const {
    execute: onBid,
    status: placeBidStatus,
    value: onBidResponse,
  } = useAsync(placeBid(bidAmount, placeBidUrl, "bid", isToUseAuthZero, accessToken), false);

  const {
    execute: buyNow,
    status: buyNowStatus,
    value: buyNowResponse,
  } = useAsync(placeBid(buyNowAmount, placeBidUrl, "buy", isToUseAuthZero, accessToken), false);

  const {
    execute: acceptTC,
    value: acceptTermsAndConditionsResponse,
    status: acceptTermsAndConditionsStatus,
  } = useAsync(
    () =>
      acceptTermsAndConditions(
        acceptTermsAndConditionsUrl,
        "put",
        isToUseAuthZero,
        accessToken
      ),
    false
  );

  useEffect(() => {
    if (!bidInfo) {
      return;
    }
    setInfo(bidInfo);
  }, [bidInfo]);

  useEffect(() => {
    if (!biddingInfoResponse) {
      return;
    }
    setInfo(biddingInfoResponse);
  }, [biddingInfoResponse]);

  useEffect(() => {
    if (!onBidResponse?.biddingInformation) {
      return;
    }
    setInfo(onBidResponse?.biddingInformation);
  }, [onBidResponse]);

  useEffect(() => {
    if (!buyNowResponse?.bidResult) {
      return;
    }
    setInfo(buyNowResponse?.biddingInformation);
  }, [buyNowResponse]);

  useEffect(() => {
    if (!info) {
      return;
    }

    const { currentBidAmount, startingBidAmount, bidIncrement } = info;
    const nextPossibleBid =
      currentBidAmount > 0
        ? currentBidAmount + bidIncrement
        : startingBidAmount;

    //prepopulates price input with first available value
    setBidAmount(nextPossibleBid);
  }, [
    info,
    info?.currentBidAmount,
    info?.startingBidAmount,
    info?.bidIncrement,
  ]);

  useEffect(() => {
    if (!acceptTermsAndConditionsResponse) {
      return;
    }

    setTermsAndConditionsNeedAccepting(
      !acceptTermsAndConditionsResponse.success
    );
  }, [acceptTermsAndConditionsResponse]);

  useEffect(() => {
    if (!termsAndConditions) {
      return;
    }

    setTermsAndConditionsNeedAccepting(
      termsAndConditions.termsAndConditionsNeedAccepting
    );
  }, [termsAndConditions]);

  useEffect(() => {
    setShowBanner(
      placeBidStatus === "error" ||
        acceptTermsAndConditionsStatus === "error" ||
        buyNowStatus === "error"
    );
  }, [placeBidStatus, acceptTermsAndConditionsStatus, buyNowStatus]);

  const showErrorsCard = !!bidInfoError || !!termsAndConditionsError;

  return (
    <>
      {!info && !showErrorsCard && <BidNowBuyNowSkeleton />}
      {info &&
        !showErrorsCard &&
        info.lotStatus === "Sold" &&
        info.userBidStatus === "WithYou" && (
          <SuccessfulBuyNow
            buyNowPrice={info.currentBidAmount}
            title="Congratulations, it’s yours!"
          />
        )}
      {info?.lotStatus === "Sold" && info?.userBidStatus === "NoBids" && (
        <SoldVehicleCard heading="Sold" headingColor="black" />
      )}
      {info && !showErrorsCard && info?.lotStatus !== "Sold" && (
        <UserBidRights
          errorMessageText="Buying Options are only available to Trade Buyers that are registered to buy online"
          userCanPlaceBid={info.userCanPlaceBid}
        >
          {info.saleStatus !== "InProgress" ? (
            <NoOptionAvailable saleStartDate={noOptionAvailableDate} />
          ) : (
            <BidNowBuyNow
              bidAmount={bidAmount}
              biddingViewType={info.biddingViewType}
              bidHistory={info.bidHistory}
              buyNowPrice={info.buyNowPrice}
              buyNowResult={buyNowResponse?.bidResult || null}
              currentBidAmount={info.currentBidAmount}
              getBiddingInfo={getBiddingInfo}
              increment={info.bidIncrement}
              myMaxProxy={info.myMaxProxy}
              onBuyNow={buyNow}
              onPlaceBid={onBid}
              onAcceptTC={acceptTC}
              reserveMet={info.reserveMet}
              saleEndDate={info.saleEndDate}
              saleStatus={info.saleStatus}
              setBidAmount={setBidAmount}
              startingBidAmount={info.startingBidAmount}
              userBidStatus={info.userBidStatus}
              termsAndConditionsNeedAccepting={termsAndConditionsNeedAccepting}
              termsAndConditionsLink={
                termsAndConditions?.termsAndConditionsLink
              }
              setInfo={setInfo}
              isTracked={isTracked}
              vrm={vrm}
              setIsTracked={setIsTracked}
              lotStatus={info?.lotStatus}
              inventoryId={inventoryId}
              isToUseAuthZero={isToUseAuthZero}
              accessToken={accessToken}
            />
          )}
        </UserBidRights>
      )}

      {showErrorsCard && (
        <ErrorState
          icon="exclamation-triangle"
          text="Buying Options are not available at the moment"
          iconWeight="solid"
        />
      )}
      {showBanner && (
        <ErrorPlaceBNBN
          closeBanner={() => setShowBanner(false)}
          placeBidStatus={placeBidStatus}
        />
      )}
    </>
  );
};

export default BidNowBuyNowWithFetch;
